<script setup>
import {onMounted, ref} from "vue";
import getElementSize from "../lib/utils/getElementSize.js";
import addEventListeners from "../lib/utils/addEventListeners.js";

const props = defineProps(['images']);

const fixedContainer = ref();
const sizeableContainer = ref();
const elementTemplate = ref();

const translatable_distance = ref(0);
const image_width = ref(0);

const nbr_of_dots = ref(0);
const current_index = ref(0);

const min_windows_width = 540;

function handlePrevClick() {
    translateTo( -1 );
}

function handleNextClick() {
    translateTo( 1 );
}

function handleDotClick(new_index) {
    translateTo( 1, new_index - 1 );
}

function translateTo(direction, new_index = null) {
    const fixedWidth = getElementSize( fixedContainer.value ).width;
    const maxWidth = getElementSize( sizeableContainer.value ).width;
    const left = fixedContainer.value.scrollLeft;

    let scrollTo;

    if ( new_index !== null ) {
        scrollTo = fixedWidth * new_index;
    } else {
        scrollTo = left + ( fixedWidth * direction );
    }

    if ( scrollTo >= maxWidth ) {
        scrollTo = 0;
    } else if ( scrollTo <= ( 0 - fixedWidth ) ) {
        scrollTo = maxWidth;
    }

    fixedContainer.value.scrollTo( {left: scrollTo, behavior: "smooth"} );
}

function computeSliderWidth() {
    if ( !props.images ) {
        return;
    }

    const compute_gap = ( props.images.length - 1 ) * 20;
    const width = getElementSize( elementTemplate.value ).width;
    const max_width = width * props.images.length;

    sizeableContainer.value.style.width = max_width + compute_gap + 'px';

    image_width.value = width;

    if ( window.innerWidth <= min_windows_width ) {
        nbr_of_dots.value = props.images.length;
        translatable_distance.value = width + 20;
    } else {
        translatable_distance.value = ( width * 3 ) + 40;
        nbr_of_dots.value = Math.floor( max_width / translatable_distance.value ) + 1;
    }

    current_index.value = 0;
    translateTo( 0 );
}

onMounted( () => {
    computeSliderWidth();

    fixedContainer.value.addEventListener('scrollend', e => {
        const fixedWidth = getElementSize( fixedContainer.value ).width;
        const maxWidth = getElementSize( sizeableContainer.value ).width;
        const left = fixedContainer.value.scrollLeft;

        current_index.value = 2 - ( Math.ceil( ( maxWidth / ( left + ( fixedWidth ) ) ) ) - 1 );
    } );
} );
window.addEventListener('resize', computeSliderWidth );

const current_image_index = ref(null);

let remove_escape_listener = null;

function handlePrevImageClick() {
    let new_value = current_image_index.value - 1;

    if ( new_value < 0 ) {
        new_value = props.images.length - 1;
    }

    current_image_index.value = new_value;
}

function handleNextImageClick() {
    let new_value = current_image_index.value + 1;

    if ( new_value === props.images.length ) {
        new_value = 0;
    }

    current_image_index.value = new_value;
}

function handleCloseModalImage() {
    current_image_index.value = null;
    document.body.classList.remove('overflow-hidden');
    remove_escape_listener.keyup();
}

function handleModalImage(index) {
    current_image_index.value = index;
    document.body.classList.add('overflow-hidden');

    remove_escape_listener = addEventListeners( document, {
        keyup(event) {
            if ( event.key === 'Escape' ) {
                handleCloseModalImage();
            }

            if ( event.key === 'ArrowLeft' ) {
                handlePrevImageClick();
            }

            if ( event.key === 'ArrowRight' ) {
                handleNextImageClick();
            }
        }
    } );
}
</script>

<template>
    <div v-if="props.images">
        <div class="overflow-hidden mb-5">
            <div class="overflow-scroll" ref="fixedContainer">
                <div class="flex flex-row flex-nowrap gap-x-5 transition-transform duration-300 will-change-transform" ref="sizeableContainer">
                    <button v-for="($image, index) in props.images"
                            @click="() => handleModalImage( index )"
                        class="inline-block h-80 bg-cover bg-center"
                        :style="{backgroundImage: 'url(' + $image['sizes']['medium_large'] + ')', width: image_width + 'px'}" :title="'Image ' + index" :aria-label="'Image ' + index">
                    </button>
                </div>

                <div class="w-5/12 min-w-[240px] max-w-[480px] h-px" ref="elementTemplate"></div>
            </div>
        </div>

        <div class="relative" v-if="nbr_of_dots > 1">
            <div class="w-2/12 flex flex-row flex-nowrap gap-x-1 relative z-10">
                <button @click="handlePrevClick" title="Images précédentes" aria-label="Images précédentes"
                        class="bg-orange/75 rounded-full text-white text-xl px-5 py-1 transition-colors hover:bg-orange">
                    <i class="fa-solid fa-chevron-left"></i>
                </button>

                <button @click="handleNextClick" title="Images suivantes" aria-label="Images suivantes"
                    class="bg-orange/75 rounded-full text-white text-xl px-5 py-1 transition-colors hover:bg-orange">
                    <i class="fa-solid fa-chevron-right"></i>
                </button>
            </div>

            <ul class="w-full absolute inset-0 hidden sm:flex flex-row flex-nowrap gap-x-2 justify-center">
                <li v-for="i in nbr_of_dots" class="flex flex-col place-content-center">
                    <button @click="() => handleDotClick( i )" :title="'Partie ' + i + ' du carousel'" :aria-label="'Partie ' + i + ' du carousel'"
                        class="block w-2 h-2 p-2 rounded-full"
                        :class="[ {
                            'bg-white': i - 1 === current_index,
                            'bg-orange': i - 1 !== current_index
                        } ]"
                    ></button>
                </li>
            </ul>
        </div>
    </div>

    <Teleport to="body">
        <Transition>
            <div v-if="current_image_index !== null" class="bg-black/65 fixed inset-0 z-50">
                <button class="text-white text-3xl absolute right-5 top-5" @click="handleCloseModalImage" title="Fermer la modal" aria-label="Fermer la modal" tabindex="-1">
                    <i class="fa-solid fa-xmark"></i>
                </button>
                <button class="text-white text-3xl absolute left-5 top-1/2 -translate-y-1/2" @click="handlePrevImageClick" title="Image précédente" aria-label="Image précédente" tabindex="-1">
                    <i class="fa-solid fa-chevron-left"></i>
                </button>
                <button class="text-white text-3xl absolute right-5 top-1/2 -translate-y-1/2" @click="handleNextImageClick" title="Image suivante" aria-label="Image suivante" tabindex="-1">
                    <i class="fa-solid fa-chevron-right"></i>
                </button>
                <div class="flex flex-col place-content-center justify-center w-full h-full">
                    <img :src="props.images[ current_image_index ]['sizes']['2048x2048']" alt="" class="block w-auto max-w-screen h-auto max-h-screen py-10 px-12 mx-auto">
                </div>
            </div>
        </Transition>
    </Teleport>
</template>